import { useEditMode } from "@/context/EditModeContext";
import { TextBlockSection } from "@/components/site/edit/TextBlockSection";
import { Eye, EyeOff, ArrowUp, ArrowDown, Plus } from "lucide-react";

// Wraps a page section. In edit mode it shows a control bar (hide/show, move,
// add text below) and dims hidden sections. On the public site, hidden sections
// don't render. It also renders any custom text blocks placed after this section.
export function SectionShell({ id, label, order, isFirst, isLast, noReorder = false, children }) {
  const em = useEditMode();
  const hidden = em.isHidden(id);
  const trailing = (em.blocks || []).filter((b) => b.after === id);

  if (!em.editing) {
    return (
      <>
        {!hidden && children}
        {trailing.map((b) => <TextBlockSection key={b.id} block={b} />)}
      </>
    );
  }

  return (
    <>
      <div data-testid={`section-shell-${id}`} className="relative">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[500] flex items-center gap-1 rounded-full bg-[#0F172A]/90 px-2 py-1 text-white shadow-lg backdrop-blur">
          <span className="px-2 text-xs font-semibold tracking-wide">{label}</span>
          <button title={hidden ? "Show section" : "Hide section"} data-testid={`section-hide-${id}`}
            onClick={() => em.toggleHidden(id)} className="grid place-items-center w-7 h-7 rounded-full hover:bg-white/15">
            {hidden ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
          {!noReorder && (
            <>
              <button title="Move up" disabled={isFirst} data-testid={`section-up-${id}`}
                onClick={() => em.moveSection(order, id, -1)} className="grid place-items-center w-7 h-7 rounded-full hover:bg-white/15 disabled:opacity-30">
                <ArrowUp size={14} />
              </button>
              <button title="Move down" disabled={isLast} data-testid={`section-down-${id}`}
                onClick={() => em.moveSection(order, id, 1)} className="grid place-items-center w-7 h-7 rounded-full hover:bg-white/15 disabled:opacity-30">
                <ArrowDown size={14} />
              </button>
            </>
          )}
          <button title="Add a text section below" data-testid={`section-add-${id}`}
            onClick={() => em.addTextBlock(id)} className="grid place-items-center h-7 rounded-full hover:bg-white/15 px-2 text-xs">
            <span className="flex items-center gap-1"><Plus size={13} /> Text</span>
          </button>
        </div>
        <div className={hidden ? "opacity-40 grayscale pointer-events-none" : ""}>
          {children}
        </div>
      </div>
      {trailing.map((b) => <TextBlockSection key={b.id} block={b} />)}
    </>
  );
}
